import { useState, useCallback } from 'react';
import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { API_URL } from '../lib/api-client';

interface UseApiRequestOptions {
  showErrorToast?: boolean;
  showSuccessToast?: boolean;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

interface UseApiRequestReturn<T = any> {
  data: T | null;
  loading: boolean;
  error: string | null;
  execute: (endpoint: string, config?: AxiosRequestConfig) => Promise<ApiResponse<T>>;
  reset: () => void;
}

/**
 * Custom hook for making API requests with automatic token handling
 * @param options - Configuration options for the hook
 * @returns Object containing data, loading state, error, and execute function
 * 
 * @example
 * const { data, loading, error, execute } = useApiRequest();
 * 
 * // GET request
 * await execute(API_ENDPOINTS.PROFILE.GET_MY_PROFILE);
 * 
 * // POST request
 * await execute(API_ENDPOINTS.AUTH.LOGIN, {
 *   method: 'POST',
 *   data: { email, password }
 * });
 * 
 * // With params
 * await execute(API_ENDPOINTS.ADMIN.GET_USERS, {
 *   params: { page: 1, limit: 20 }
 * });
 */
export function useApiRequest<T = any>(_options: UseApiRequestOptions = {}): UseApiRequestReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(
    async (endpoint: string, config: AxiosRequestConfig = {}): Promise<ApiResponse<T>> => {
      try {
        setLoading(true);
        setError(null);

        // Get token from localStorage
        const token = localStorage.getItem('token');

        // Create axios instance with auth header
        const axiosInstance = axios.create({
          baseURL: API_URL,
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
            ...config.headers,
          },
        });

        // Make the request
        const response = await axiosInstance({
          url: endpoint,
          method: config.method || 'GET',
          ...config,
        });

        // Handle response
        const responseData = response.data;
        setData(responseData.data || responseData);

        return {
          success: true,
          data: responseData.data || responseData,
          message: responseData.message,
        };
      } catch (err) {
        const axiosError = err as AxiosError<any>;
        const errorMessage =
          axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          axiosError.message ||
          'An error occurred';

        setError(errorMessage);

        // Handle 401 Unauthorized
        if (axiosError.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }

        return {
          success: false,
          error: errorMessage,
          message: errorMessage,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { data, loading, error, execute, reset };
}

/**
 * Custom hook for making GET requests
 * @param endpoint - API endpoint to call
 * @param config - Axios request configuration
 * @returns Object containing data, loading state, error, and refetch function
 */
export function useGet<T = any>(endpoint: string, config?: AxiosRequestConfig) {
  const { data, loading, error, execute } = useApiRequest<T>();
  const [initialized, setInitialized] = useState(false);

  const refetch = useCallback(async () => {
    await execute(endpoint, { ...config, method: 'GET' });
  }, [endpoint, config, execute]);

  // Auto-fetch on mount
  if (!initialized) {
    setInitialized(true);
    refetch();
  }

  return { data, loading, error, refetch };
}

/**
 * Custom hook for making POST requests
 * @returns Object containing loading state, error, and post function
 */
export function usePost<T = any>() {
  const { data, loading, error, execute } = useApiRequest<T>();

  const post = useCallback(
    async (endpoint: string, body: any, config?: AxiosRequestConfig) => {
      return await execute(endpoint, {
        ...config,
        method: 'POST',
        data: body,
      });
    },
    [execute]
  );

  return { data, loading, error, post };
}

/**
 * Custom hook for making PUT requests
 * @returns Object containing loading state, error, and put function
 */
export function usePut<T = any>() {
  const { data, loading, error, execute } = useApiRequest<T>();

  const put = useCallback(
    async (endpoint: string, body: any, config?: AxiosRequestConfig) => {
      return await execute(endpoint, {
        ...config,
        method: 'PUT',
        data: body,
      });
    },
    [execute]
  );

  return { data, loading, error, put };
}

/**
 * Custom hook for making PATCH requests
 * @returns Object containing loading state, error, and patch function
 */
export function usePatch<T = any>() {
  const { data, loading, error, execute } = useApiRequest<T>();

  const patch = useCallback(
    async (endpoint: string, body: any, config?: AxiosRequestConfig) => {
      return await execute(endpoint, {
        ...config,
        method: 'PATCH',
        data: body,
      });
    },
    [execute]
  );

  return { data, loading, error, patch };
}

/**
 * Custom hook for making DELETE requests
 * @returns Object containing loading state, error, and del function
 */
export function useDelete<T = any>() {
  const { data, loading, error, execute } = useApiRequest<T>();

  const del = useCallback(
    async (endpoint: string, config?: AxiosRequestConfig) => {
      return await execute(endpoint, {
        ...config,
        method: 'DELETE',
      });
    },
    [execute]
  );

  return { data, loading, error, del };
}

export default useApiRequest;